const getGS = () => window.__canvasGlobalScope
const getIframeWindow = () => {
  const iframe = document.getElementById('canvas-iframe')
  return iframe?.contentWindow || window
}

export const commandDefs = [
  {
    name: 'zoom',
    description: 'Set zoom level',
    args: ['25', '50', '75', '100', '150', '200']
  },
  {
    name: 'mode',
    description: 'Switch canvas mode',
    args: ['default', 'inspect', 'panning', 'interact']
  },
  { name: 'editor', description: 'Toggle QuickEditor', args: null },
  { name: 'settings', description: 'Open settings', args: null },
  {
    name: 'go',
    description: 'Navigate to route',
    args: ['dashboard', 'canvas', 'settings']
  },
  { name: 'open', description: 'Open in explorer', args: 'dynamic' },
  { name: 'select', description: 'Select on canvas', args: 'dynamic' },
  { name: 'search', description: 'Search canvas', args: null },
  { name: 'docs', description: 'Open documentation', args: null },
  { name: 'theme', description: 'Toggle theme', args: ['dark', 'light'] },
  // Canvas v2 commands.
  {
    name: 'auto-layout',
    description: 'Cluster items by route + tile',
    args: null
  },
  { name: 'fit', description: 'Fit selection (or all) to view', args: null },
  { name: 'reset', description: 'Reset viewport to 35%', args: null },
  { name: 'undo', description: 'Undo last canvas change', args: null },
  { name: 'redo', description: 'Redo', args: null },
  { name: 'duplicate', description: 'Duplicate selection', args: null },
  { name: 'delete', description: 'Delete selection', args: null },
  { name: 'group', description: 'Group selection', args: null },
  { name: 'ungroup', description: 'Ungroup selection', args: null },
  {
    name: 'snap',
    description: 'Toggle snap',
    args: ['on', 'off', 'grid', 'items', 'guides']
  },
  {
    name: 'grid-step',
    description: 'Set snap-grid step (canvas px)',
    args: 'dynamic'
  },
  {
    name: 'frame-mode',
    description: 'Toggle live iframe previews',
    args: ['on', 'off']
  },
  { name: 'guides', description: 'Manage guides', args: ['clear'] },
  { name: 'shortcuts', description: 'Show shortcuts overlay', args: null },
  { name: 'help', description: 'Show shortcuts overlay', args: null },
  {
    name: 'align',
    description: 'Align selection',
    args: ['left', 'right', 'top', 'bottom', 'center', 'middle']
  },
  {
    name: 'distribute',
    description: 'Distribute selection equally',
    args: ['horizontal', 'vertical']
  }
]

export const commands = {
  zoom(el, args) {
    const value = parseFloat(args[0])
    if (!value || value <= 0) return 'Usage: /zoom [percentage]'
    const gs = getGS()
    if (!gs?.panzoomInstance) return 'No canvas instance'
    const win = getIframeWindow()
    const { x, y } = gs.positions
    gs.panzoomInstance.smoothZoomAbs(
      x + win.innerWidth / 2,
      y + win.innerHeight / 2,
      value / 100
    )
    return `Zoom set to ${value}%`
  },

  mode(el, args) {
    const modes = ['defaultMode', 'inspectMode', 'panningMode', 'interactMode']
    const arg = (args[0] || '').toLowerCase()
    if (!arg) return 'Usage: /mode [default|inspect|panning|interact]'
    // Accept short names ('interact' → 'interactMode'); existing chrome
    // speaks v1-style flags which methods/index.js bridges to v2.
    const resolved = modes.find((m) => m.toLowerCase().startsWith(arg))
    if (!resolved) return 'Unknown mode'
    el.setCanvasMode(resolved)
    return `Switched to ${resolved.replace('Mode', '')}`
  },

  editor(el) {
    const cs = el.getCanvasScope()
    cs.editorActive = !cs.editorActive
    return `Editor ${cs.editorActive ? 'opened' : 'closed'}`
  },

  settings(el) {
    el.call('openModal', '/settings', { key: '/settings' })
    return 'Settings opened'
  },

  go(el, args) {
    const path = args[0]
    if (!path) return 'Usage: /go [path]'
    el.router(path, el.getRoot())
    return `Navigated to ${path}`
  },

  open(el, args) {
    const key = args[0]
    if (!key) return 'Usage: /open [key]'
    el.call('setWindow', 'explorer', key)
    return `Opened ${key}`
  },

  select(el, args) {
    const key = args[0]
    if (!key) return 'Usage: /select [key]'
    const rs = el.getRootState()
    const data = rs?.data
    const type = data?.pages?.[key] ? 'pages' : 'components'
    el.call('openArtboardItem', type, key)
    return `Selected ${key}`
  },

  search(el, args) {
    const query = args.join(' ')
    if (!query) return 'Usage: /search [query]'
    el.call('setWindow', 'explorer', 'search:' + query)
    return `Searching: ${query}`
  },

  docs(el) {
    el.call('setWindow', 'explorer', 'onboarding')
    return 'Docs opened'
  },

  theme(el, args) {
    const target = args[0]
    if (target && target !== 'dark' && target !== 'light')
      return 'Usage: /theme [dark|light]'
    el.call('toggleTheme', target || undefined)
    return 'Theme toggled'
  },

  // ── Canvas v2 commands ────────────────────────────────────────────
  'auto-layout': (el) => {
    el.call('autoLayout')
    return 'Auto-laid out'
  },
  fit: (el) => {
    el.call('fitToSelection')
    return 'Fit to selection'
  },
  reset: (el) => {
    el.call('resetCanvasView')
    return 'View reset'
  },
  undo: (el) => {
    el.call('undo')
    return 'Undone'
  },
  redo: (el) => {
    el.call('redo')
    return 'Redone'
  },
  duplicate: (el) => {
    el.call('duplicateSelection')
    return 'Duplicated'
  },
  delete: (el) => {
    el.call('deleteSelection')
    return 'Deleted'
  },
  group: (el) => {
    el.call('group')
    return 'Grouped'
  },
  ungroup: (el) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return 'No canvas'
    const ids = v2.selection.ids
    Object.entries(v2.groups || {})
      .filter(([, g]) => g.children.some((c) => ids.includes(c)))
      .forEach(([gid]) => el.call('ungroup', gid))
    return 'Ungrouped'
  },
  snap: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return 'No canvas'
    const a = (args[0] || '').toLowerCase()
    if (a === 'on') {
      el.call('patchSnap', { enabled: true })
      return 'Snap on'
    }
    if (a === 'off') {
      el.call('patchSnap', { enabled: false })
      return 'Snap off'
    }
    if (a === 'grid') {
      el.call('patchSnap', { toGrid: !v2.snap.toGrid })
      return `Grid snap ${!v2.snap.toGrid ? 'on' : 'off'}`
    }
    if (a === 'items') {
      el.call('patchSnap', { toItems: !v2.snap.toItems })
      return `Item snap ${!v2.snap.toItems ? 'on' : 'off'}`
    }
    if (a === 'guides') {
      el.call('patchSnap', { toGuides: !v2.snap.toGuides })
      return `Guide snap ${!v2.snap.toGuides ? 'on' : 'off'}`
    }
    return 'Usage: /snap [on|off|grid|items|guides]'
  },
  'grid-step': (el, args) => {
    const n = Math.max(4, Math.min(500, Number(args[0]) || 0))
    if (!n) return 'Usage: /grid-step [N]'
    el.call('patchSnap', { gridStep: n })
    return `Grid step: ${n}`
  },
  'frame-mode': (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return 'No canvas'
    const target =
      args[0] === 'on' ? true : args[0] === 'off' ? false : !v2.frameMode
    el.call('patchCanvas', { frameMode: target })
    return `Frame mode ${target ? 'on' : 'off'}`
  },
  guides: (el, args) => {
    if (args[0] === 'clear') {
      el.call('patchCanvas', { guides: { horizontal: [], vertical: [] } })
      return 'Guides cleared'
    }
    return 'Usage: /guides clear'
  },
  shortcuts: (el) => {
    el.call('openKeyboardShortcuts')
    return 'Shortcuts overlay'
  },
  help: (el) => {
    el.call('openKeyboardShortcuts')
    return 'Shortcuts overlay'
  },

  align: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return 'No canvas'
    const ids = v2.selection.ids
    if (ids.length < 2) return 'Select 2+ items first'
    const dir = (args[0] || '').toLowerCase()
    const items = v2.items
    const rects = ids.map((id) => items[id]).filter(Boolean)
    const nodeMap = buildCanvasItemNodeMap()
    let target
    if (dir === 'left') {
      const minX = Math.min(...rects.map((r) => r.x))
      target = () => ({ x: minX })
    } else if (dir === 'right') {
      const maxRight = Math.max(...rects.map((r) => r.x + r.w))
      target = (r) => ({ x: maxRight - r.w })
    } else if (dir === 'top') {
      const minY = Math.min(...rects.map((r) => r.y))
      target = () => ({ y: minY })
    } else if (dir === 'bottom') {
      const maxBottom = Math.max(...rects.map((r) => r.y + r.h))
      target = (r) => ({ y: maxBottom - r.h })
    } else if (dir === 'center') {
      const cx =
        (Math.min(...rects.map((r) => r.x)) +
          Math.max(...rects.map((r) => r.x + r.w))) /
        2
      target = (r) => ({ x: cx - r.w / 2 })
    } else if (dir === 'middle') {
      const cy =
        (Math.min(...rects.map((r) => r.y)) +
          Math.max(...rects.map((r) => r.y + r.h))) /
        2
      target = (r) => ({ y: cy - r.h / 2 })
    } else return 'Usage: /align [left|right|top|bottom|center|middle]'
    ids.forEach((id) => {
      const r = items[id]
      if (!r) return
      const patch = target(r)
      el.call('patchCanvasItem', id, patch)
      applyItemNodePatch(nodeMap.get(id), patch)
    })
    el.call('refreshCanvasChrome')
    el.call('pushHistory')
    el.call('persistCanvasLayout')
    return `Aligned ${dir}`
  },

  distribute: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return 'No canvas'
    const ids = v2.selection.ids
    if (ids.length < 3) return 'Select 3+ items first'
    const axisArg = (args[0] || '').toLowerCase()
    const axis = axisArg.startsWith('h')
      ? 'h'
      : axisArg.startsWith('v')
        ? 'v'
        : null
    if (!axis) return 'Usage: /distribute [horizontal|vertical]'
    const items = v2.items
    const sorted = [...ids]
      .filter((id) => items[id])
      .sort((a, b) =>
        axis === 'h' ? items[a].x - items[b].x : items[a].y - items[b].y
      )
    const first = items[sorted[0]]
    const last = items[sorted[sorted.length - 1]]
    const nodeMap = buildCanvasItemNodeMap()
    const axisKey = axis === 'h' ? 'x' : 'y'
    const sizeKey = axis === 'h' ? 'w' : 'h'
    const total = last[axisKey] + last[sizeKey] - first[axisKey]
    const sum = sorted.reduce((acc, id) => acc + items[id][sizeKey], 0)
    const gap = (total - sum) / (sorted.length - 1)
    let cursor = first[axisKey]
    sorted.forEach((id) => {
      const r = items[id]
      const patch = { [axisKey]: cursor }
      el.call('patchCanvasItem', id, patch)
      applyItemNodePatch(nodeMap.get(id), patch)
      cursor += r[sizeKey] + gap
    })
    el.call('refreshCanvasChrome')
    el.call('pushHistory')
    el.call('persistCanvasLayout')
    return `Distributed ${axis === 'h' ? 'horizontally' : 'vertically'}`
  }
}

// One DOM query per invocation — avoids N selector scans inside the
// forEach loop below. patchCanvasItem is a non-reactive in-place
// mutation, so we still need the imperative style write for immediate
// visual feedback.
function buildCanvasItemNodeMap() {
  const map = new Map()
  document.querySelectorAll('[data-canvas-item]').forEach((n) => {
    map.set(n.getAttribute('data-canvas-item'), n)
  })
  return map
}

function applyItemNodePatch(node, patch) {
  if (!node) return
  if (patch.x !== undefined) node.style.left = patch.x + 'px'
  if (patch.y !== undefined) node.style.top = patch.y + 'px'
}

function runCommand(bar, input) {
  const value = (
    typeof input === 'string' ? input : bar.Input.node.value
  ).trim()
  if (!value || !value.startsWith('/')) return
  const parts = value.slice(1).split(/\s+/)
  const cmd = parts[0].toLowerCase()
  const args = parts.slice(1)
  const handler = commands[cmd]
  const result = handler ? handler(bar, args) : `Unknown: /${cmd}`

  bar.Input.node.value = ''
  hideDropdown(bar)
  bar.Input.node.blur()

  if (bar.data.feedbackTimer) clearTimeout(bar.data.feedbackTimer)
  if (bar.Feedback?.node) {
    bar.Feedback.node.textContent = result
    bar.Feedback.node.style.opacity = '1'
    bar.data.feedbackTimer = setTimeout(() => {
      if (bar.Feedback?.node) bar.Feedback.node.style.opacity = '0'
    }, 3000)
  }
}

function getDynamicArgs(bar, cmd) {
  const items = []
  try {
    const rs = bar.getRootState()
    const data = rs?.data
    if (!data) return items

    const categories = [
      { key: 'pages', label: 'Pages' },
      { key: 'components', label: 'Components' },
      { key: 'functions', label: 'Functions' }
    ]

    for (const cat of categories) {
      const obj = data[cat.key]
      if (!obj || typeof obj !== 'object') continue
      const keys = Object.keys(obj)
      if (!keys.length) continue
      items.push({ label: cat.label, type: 'header' })
      for (const k of keys) {
        items.push({
          label: k,
          description: cat.key.slice(0, -1),
          type: 'arg',
          fullValue: '/' + cmd + ' ' + k
        })
      }
    }
  } catch (e) {
    /* rootState may not be available */
  }
  return items
}

function getFilteredItems(bar, value) {
  const raw = value || ''
  const trimmed = raw.trim()

  if (!trimmed || !trimmed.startsWith('/')) {
    return commandDefs.map((c) => ({
      label: '/' + c.name,
      description: c.description,
      type: 'command'
    }))
  }

  const parts = trimmed.slice(1).split(/\s+/)
  const cmd = parts[0].toLowerCase()
  const hasSpace = raw.includes(' ')

  if (hasSpace) {
    const def = commandDefs.find((c) => c.name === cmd)
    if (!def) return []

    if (def.args === 'dynamic') {
      const all = getDynamicArgs(bar, cmd)
      const argFilter = (parts[1] || '').toLowerCase()
      if (!argFilter) return all
      return all.filter((a) =>
        a.type === 'header' ? false : a.label.toLowerCase().includes(argFilter)
      )
    }

    if (!def.args)
      return [
        {
          label: '/' + cmd,
          description: 'Run ' + def.description.toLowerCase(),
          type: 'run',
          fullValue: '/' + cmd
        }
      ]
    const argFilter = (parts[1] || '').toLowerCase()
    return def.args
      .filter((a) => !argFilter || a.startsWith(argFilter))
      .map((a) => ({
        label: a,
        description: '',
        type: 'arg',
        fullValue: '/' + cmd + ' ' + a
      }))
  }

  return commandDefs
    .filter((c) => c.name.startsWith(cmd))
    .map((c) => ({
      label: '/' + c.name,
      description: c.description,
      type: 'command'
    }))
}

function renderDropdown(bar, items, activeIndex) {
  const dropdown = bar.CmdDropdown?.node
  if (!dropdown) return
  dropdown.textContent = ''

  if (!items.length) {
    dropdown.style.opacity = '0'
    dropdown.style.visibility = 'hidden'
    return
  }

  let selectableIndex = 0
  items.forEach((item) => {
    if (item.type === 'header') {
      const header = document.createElement('div')
      header.style.cssText =
        'padding:4px 8px 2px;font-size:0.8em;opacity:0.35;text-transform:uppercase;' +
        'letter-spacing:0.05em;pointer-events:none;user-select:none;'
      header.textContent = item.label
      dropdown.appendChild(header)
      return
    }

    const idx = selectableIndex++
    const row = document.createElement('div')
    row.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;' +
      'padding:4px 8px;cursor:pointer;border-radius:4px;gap:8px;' +
      (idx === activeIndex ? 'background:rgba(255,255,255,0.08);' : '')
    row.dataset.index = idx
    row.dataset.itemIndex = items.indexOf(item)

    const left = document.createElement('span')
    left.style.cssText = 'font-weight:500;white-space:nowrap;'
    left.textContent = item.label

    row.appendChild(left)

    if (item.description) {
      const right = document.createElement('span')
      right.style.cssText =
        'opacity:0.4;font-size:0.9em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'
      right.textContent = item.description
      row.appendChild(right)
    }

    dropdown.appendChild(row)
  })
}

function getSelectableItems(items) {
  return items.filter((i) => i.type !== 'header')
}

function updateDropdown(bar) {
  const value = bar.Input?.node?.value || ''
  const items = getFilteredItems(bar, value)
  bar.data.filteredItems = items
  bar.data.selectableItems = getSelectableItems(items)
  bar.data.activeIndex = -1
  renderDropdown(bar, items, -1)
}

function showDropdown(bar) {
  updateDropdown(bar)
  const dropdown = bar.CmdDropdown?.node
  if (dropdown) {
    dropdown.style.opacity = '1'
    dropdown.style.visibility = 'visible'
    dropdown.style.pointerEvents = 'auto'
  }
  bar.data.isOpen = true
}

function hideDropdown(bar) {
  const dropdown = bar.CmdDropdown?.node
  if (dropdown) {
    dropdown.style.opacity = '0'
    dropdown.style.visibility = 'hidden'
    dropdown.style.pointerEvents = 'none'
  }
  bar.data.activeIndex = -1
  bar.data.isOpen = false
}

export const CommandBar = {
  extends: 'Navbar',
  position: 'relative',
  gap: '0',
  round: 'C1',
  userSelect: 'none',
  cursor: 'text',
  minWidth: 'G+C',
  padding: '0',

  data: {
    feedbackTimer: null,
    filteredItems: [],
    selectableItems: [],
    activeIndex: -1,
    isOpen: false
  },

  onRender: (el) => {
    if (el.__cmdBarInit) return
    el.__cmdBarInit = true

    el.node.addEventListener('click', (e) => {
      const row = e.target.closest('[data-index]')
      if (!row) return
      e.stopPropagation()
      const itemIdx = parseInt(row.dataset.itemIndex)
      const item = el.data.filteredItems[itemIdx]
      if (!item) return
      if (item.type === 'run' || item.type === 'arg') {
        runCommand(el, item.fullValue)
      } else {
        el.Input.node.value = item.label + ' '
        showDropdown(el)
        el.Input.node.focus()
      }
    })

    document.addEventListener('mousedown', (e) => {
      // el.node is null once the CommandBar unmounts (e.g. route change);
      // the listener outlives the element, so guard against that.
      if (!el.node) return
      if (!el.node.contains(e.target)) {
        hideDropdown(el)
        el.Input?.node?.blur?.()
      }
    })
  },

  onClick: (e, el) => {
    el.Input.node.focus()
    if (!el.data.isOpen) showDropdown(el)
  },

  Icon: {
    name: 'chevronRight',
    width: 'A',
    height: 'A',
    margin: '- -B - -',
    opacity: '.35',
    flexShrink: '0',
    marginLeft: 'Z',
    pointerEvents: 'none'
  },

  Input: {
    tag: 'input',
    type: 'text',
    placeholder: 'Search or /command',
    theme: null,
    border: '0',
    outline: 'none',
    background: 'transparent',
    color: 'currentColor',
    fontFamily: 'inherit',
    width: '100%',
    padding: 'Z Z Z B2',
    flexGrow: '1',

    '::placeholder': {
      color: 'currentColor',
      opacity: '.25'
    },

    onInput: (e, el) => {
      const bar = el.parent
      updateDropdown(bar)
    },

    onKeyDown: (e, el) => {
      const bar = el.parent
      const selectable = bar.data.selectableItems || []
      let idx = bar.data.activeIndex

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!bar.data.isOpen) {
          showDropdown(bar)
          return
        }
        idx = idx < selectable.length - 1 ? idx + 1 : 0
        bar.data.activeIndex = idx
        renderDropdown(bar, bar.data.filteredItems, idx)
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!bar.data.isOpen) {
          showDropdown(bar)
          return
        }
        idx = idx > 0 ? idx - 1 : selectable.length - 1
        bar.data.activeIndex = idx
        renderDropdown(bar, bar.data.filteredItems, idx)
        return
      }

      if (e.key === 'Tab') {
        e.preventDefault()
        const item = selectable[idx >= 0 ? idx : 0]
        if (!item) return
        el.node.value = item.type === 'arg' ? item.fullValue : item.label + ' '
        updateDropdown(bar)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (idx >= 0 && selectable[idx]) {
          const item = selectable[idx]
          if (item.type === 'run') {
            runCommand(bar, item.fullValue)
          } else if (item.type === 'arg') {
            runCommand(bar, item.fullValue)
          } else {
            el.node.value = item.label + ' '
            bar.data.activeIndex = -1
            showDropdown(bar)
          }
          return
        }
        if (!el.node.value.trim()) return
        runCommand(bar, el.node.value)
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        if (el.node.value) {
          el.node.value = ''
          updateDropdown(bar)
        } else {
          hideDropdown(bar)
          el.node.blur()
        }
      }
    }
  },

  CmdDropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    marginTop: 'Y',
    flow: 'y',
    gap: 'W',
    padding: 'Z',
    round: 'Z2',
    theme: 'canvas-card-glass',
    fontSize: 'Z2',
    color: 'white',
    opacity: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'A opacity defaultBezier',
    zIndex: '1000',
    maxHeight: 'H',
    overflow: 'hidden auto',
    scrollBehavior: 'smooth'
  },

  Feedback: {
    tag: 'span',
    position: 'absolute',
    top: '100%',
    left: '0',
    marginTop: 'Y',
    fontSize: 'Z2',
    color: 'white',
    theme: 'canvas-card-glass',
    padding: 'Y Z',
    round: 'Z',
    opacity: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'I',
    transition: 'B opacity defaultBezier',
    pointerEvents: 'none',
    zIndex: '1001'
  }
}
